import type { PropsWithChildren, RefObject, SyntheticEvent } from 'react';
import { useEffect, useLayoutEffect, useMemo, useRef, useContext, createContext } from 'react';

import type { Slug } from '@root/sanity/sanity.types';
import type { SanityPageAnalyticsProperties, SanityPageData } from 'types';
import { staticSiteBlockViewed, staticSiteClick } from 'utils/metrics';
import { createBlockViewDetector, type BlockIdentity, type BlockViewDetector } from './blockViewDetector';
import { getElementName } from './elementName';

type SanityPageBuilderBlock = { _key: string; _type: string; trackingLabel?: Slug };

type BlockRegistry = {
  observe: (element: Element, block: BlockIdentity) => void;
  unobserve: (element: Element) => void;
};

type ClickableElement = { elementLabel?: string; href?: string };

type SanityAnalyticsProviderProps = PropsWithChildren<{ page: SanityPageData }>;

const SanityBlockRegistryContext = createContext<BlockRegistry | null>(null);
const SanityBlockContext = createContext<BlockIdentity | null>(null);
const SanityPageContext = createContext<SanityPageData>({ id: '', slug: '', title: '', blockCount: -1 });

let currentSanityPage: SanityPageData | null = null;

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;
// Next resolves its route-change promise from a layout effect, so a passive effect here would run after `routeChangeComplete`
const useCurrentPageEffect = useIsomorphicLayoutEffect;

const blockName = (block: BlockIdentity) => block.label || block.key;

const toAnalyticsProperties = (page: SanityPageData): SanityPageAnalyticsProperties => ({
  page_id: page.id,
  page_slug: page.slug,
  page_name: page.title,
  block_count: page.blockCount,
});

const setCurrentSanityPage = (page: SanityPageData | null) => {
  currentSanityPage = page;

  if (typeof window !== 'undefined') {
    window.sanityPageProperties = page ? toAnalyticsProperties(page) : null;
  }
};

/**
 * Returns whichever Sanity page is currently mounted, or `null` when the current page isn't one
 */
export const getCurrentSanityPage = () => currentSanityPage;

/**
 * Returns the `page_id`/`page_slug`/`page_name`/`block_count` properties for the currently
 * mounted Sanity page, ready to spread into a Segment `page()` call. Empty when there isn't one.
 */
export const getSanityPageAnalyticsProperties = (): Partial<SanityPageAnalyticsProperties> => {
  const page = getCurrentSanityPage();

  return page ? toAnalyticsProperties(page) : {};
};

/**
 * Owns one block view detector for its page-builder page and turns each block view into a
 * `Static Site Block Viewed` event through the metrics module. The in-view rule lives in the
 * detector and in `docs/adr/0001-block-view-definition.md`.
 *
 * The page builder keys this provider by page id, so client-side navigation between two
 * page-builder pages starts fresh. Preview mode (`PreviewPageBuilder`) does not mount it,
 * so Sanity Studio preview sends no block tracking.
 */
export function SanityAnalyticsProvider({ page, children }: SanityAnalyticsProviderProps) {
  const detectorRef = useRef<BlockViewDetector | null>(null);

  // Blocks register from passive effects, so the detector is created in a layout effect to exist by then
  useIsomorphicLayoutEffect(() => {
    const detector = createBlockViewDetector((block) => {
      staticSiteBlockViewed({
        block_type: block.type,
        block_key: block.key,
        block_name: blockName(block),
        block_index: block.index + 1,
        page_slug: page.slug,
        page_id: page.id,
        page_name: page.title,
      });
    });

    detectorRef.current = detector;

    return () => {
      detectorRef.current = null;
      detector.dispose();
    };
  }, [page.id, page.slug, page.title]);

  const registry = useMemo<BlockRegistry>(
    () => ({
      observe: (element, block) => detectorRef.current?.observe(element, block),
      unobserve: (element) => detectorRef.current?.unobserve(element),
    }),
    [],
  );

  useCurrentPageEffect(() => {
    setCurrentSanityPage(page);

    return () => setCurrentSanityPage(null);
  }, [page]);

  return (
    <SanityPageContext.Provider value={page}>
      <SanityBlockRegistryContext.Provider value={registry}>{children}</SanityBlockRegistryContext.Provider>
    </SanityPageContext.Provider>
  );
}

function useSanityBlockTracking(block: BlockIdentity): RefObject<HTMLDivElement> {
  const registry = useContext(SanityBlockRegistryContext);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = elementRef.current;

    if (!registry || !element) {
      return;
    }

    registry.observe(element, block);

    return () => registry.unobserve(element);
  }, [registry, block]);

  return elementRef;
}

/**
 * The single registration point for page-builder blocks. The page builder wraps each item of
 * the page's top-level `pageBuilder` list in one of these, and the wrapping element is what the
 * block view detector observes. It must only wrap top-level blocks: a block nested inside another
 * block is reported as part of its enclosing page-builder block, never on its own.
 */
export function SanityTrackedBlock({ block, children, index }: PropsWithChildren<{ block: SanityPageBuilderBlock; index: number }>) {
  const { _key: key, _type: type } = block;
  const label = block.trackingLabel?.current;
  const identity = useMemo(() => ({ index, key, type, label }), [index, key, type, label]);
  const elementRef = useSanityBlockTracking(identity);

  return (
    <SanityBlockContext.Provider value={identity}>
      <div ref={elementRef}>{children}</div>
    </SanityBlockContext.Provider>
  );
}

export function useSanityInteractionTracking() {
  const block = useContext(SanityBlockContext);
  const page = useContext(SanityPageContext);

  return (ev: SyntheticEvent<Element>, { elementLabel, href }: ClickableElement) => {
    if (!block) {
      return;
    }

    staticSiteClick({
      block_type: block.type,
      block_key: block.key,
      block_name: blockName(block),
      block_index: block.index + 1,
      page_slug: page.slug,
      page_id: page.id,
      page_name: page.title,
      element_type: ev.currentTarget.tagName.toLowerCase(),
      element_name: getElementName(ev.currentTarget, elementLabel),
      element_href: href || '#',
    });
  };
}
